import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search, FileText, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';

/**
 * Branded 404 page. Replaces React Router's silent route-miss with a
 * friendly dead-end that surfaces the brand mark + the three highest-
 * intent next steps (Free ATS Check, Job Board, Dashboard) so a missed
 * URL doesn't leak users.
 */
export default function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#f4f5f7]" data-testid="not-found-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="max-w-xl w-full text-center"
      >
        <div className="flex justify-center mb-6">
          <Logo size={96} priority />
        </div>
        <div className="font-satoshi text-[112px] leading-none font-black tracking-tight text-[#002fa7] mb-2">
          404
        </div>
        <h1 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-3">
          This page isn't on the map yet
        </h1>
        <p className="text-[#4b5563] leading-relaxed mb-8">
          The page you're looking for moved, retired, or never existed.
          Pick a route below — you'll be back on track in seconds.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <Link
            to="/free-ats-check"
            className="group bg-white border border-[#e5e7eb] hover:border-[#002fa7] hover:shadow-sm transition-all p-4 text-left"
            data-testid="not-found-cta-ats"
          >
            <Search size={18} className="text-[#002fa7] mb-2" />
            <p className="text-sm font-bold text-[#0f1115]">Free ATS Check</p>
            <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1">
              Score in 60s <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
            </p>
          </Link>
          <Link
            to="/jobs"
            className="group bg-white border border-[#e5e7eb] hover:border-[#002fa7] hover:shadow-sm transition-all p-4 text-left"
            data-testid="not-found-cta-jobs"
          >
            <FileText size={18} className="text-[#002fa7] mb-2" />
            <p className="text-sm font-bold text-[#0f1115]">Job Board</p>
            <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1">
              Find matching roles <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
            </p>
          </Link>
          <Link
            to="/"
            className="group bg-white border border-[#e5e7eb] hover:border-[#002fa7] hover:shadow-sm transition-all p-4 text-left"
            data-testid="not-found-cta-home"
          >
            <Home size={18} className="text-[#002fa7] mb-2" />
            <p className="text-sm font-bold text-[#0f1115]">Home</p>
            <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1">
              Back to landing <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
            </p>
          </Link>
        </div>

        <p className="text-xs text-[#9ca3af]">
          Still stuck? Email <a className="text-[#002fa7] underline" href="mailto:hello@ascendforyou.net">hello@ascendforyou.net</a>.
        </p>
      </motion.div>
    </div>
  );
}
